import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Form, { useForm } from "antd/es/form/Form";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { EntityType, Trip } from "@mapmycustomers/shared/types/entity";
import { RawFile } from "@mapmycustomers/shared/types/File";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import AddOrCreateButton from "@app/component/preview/components/AddOrCreateButton";
import NoAccess from "@app/component/preview/components/NoAccess";
import consts from "@app/component/preview/styles/_consts.module.scss";
import ResizableDrawer from "@app/component/ResizableDrawer";
import localSettings from "@app/config/LocalSettings";
import DeleteConfirmation from "@app/scene/reports/components/MileageTracking/components/DeleteConfirmation";
import DeleteReason, {
  getDeleteReasonDisplayName,
} from "@app/scene/reports/components/MileageTracking/enum/DeleteReason";
import { deleteTrip, fetchTripPreviewData, updateTrip } from "@app/scene/reports/store/actions";
import {
  getFileIdsBeingDeleted,
  getTripPreviewRecordData,
  isDeleting,
  isTripPreviewLoading,
} from "@app/scene/reports/store/mileageTracking/selectors";
import TripRecordData from "@app/scene/reports/store/mileageTracking/TripRecordData";
import { areMileageTrackingAmountsVisible } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import tripFieldModel, { TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import { getEntityTypeDisplayName } from "@app/util/ui";
import getPageContentContainer from "@app/util/ui/getPageContentContainer";
import useHiddenChat from "@app/util/useHiddenChat";

import type EditTripFormValues from "./EditTripFormValues";
import Files from "./Files";
import FormFields from "./FormFields";
import Map from "./Map";
import RouteDetails from "./RouteDetails";
import Subheader from "./Subheader";
import styles from "./TripRecordPane.module.scss";

const messages = defineMessages({
  delete: {
    id: "reports.mileageTracking.preview.delete",
    defaultMessage: "Delete {entityName}",
    description: "Delete button in the the Trip preview pane",
  },
  files: {
    id: "reports.mileageTracking.preview.form.sections.files",
    defaultMessage: "Files{count, select, 0 {} other { ({count})}}",
    description: "Files section title on the Trip preview pane",
  },
  tripDetails: {
    id: "reports.mileageTracking.preview.form.sections.tripDetails",
    defaultMessage: "Trip Details",
    description: "Trip Details section title on the Trip preview pane",
  },
});

export interface TripRecordPaneProps {
  className?: string;
  onHide: () => void;
  onShouldShowPrompt: (shouldShow: boolean) => void;
  tripId: Trip["id"];
  zIndex?: number;
}

interface Props extends TripRecordPaneProps {
  deleting: boolean;
  filesToDeletedIds: RawFile["id"][];
  loading: boolean;
  onDelete: (payload: { reason: string }) => void;
  onFetchPreviewData: typeof fetchTripPreviewData.request;
  onUpdateTrip: typeof updateTrip.request;
  recordData: TripRecordData;
  showAmounts: boolean;
}

const TripRecordPane: React.FC<Props> = ({
  className,
  deleting,
  filesToDeletedIds,
  loading,
  onDelete,
  onFetchPreviewData,
  onHide,
  onShouldShowPrompt,
  onUpdateTrip,
  recordData,
  showAmounts,
  tripId,
  zIndex,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();
  useHiddenChat();

  const history = useHistory();
  const [detailsForm] = useForm<EditTripFormValues>();
  const [formFieldsRef, handleFinishFailed] = useErroredFieldsScroller();

  const defaultRecordPaneWidth = localSettings.getRecordPaneWidth();
  const [detailsFormChanged, setDetailsFormChanged] = useState(false);

  useEffect(() => {
    onShouldShowPrompt(detailsFormChanged);
  }, [detailsFormChanged, onShouldShowPrompt]);

  useEffect(() => {
    onFetchPreviewData(tripId);
    setDetailsFormChanged(false);
  }, [tripId, onFetchPreviewData, setDetailsFormChanged]);

  const { files, trip } = recordData;
  useEffect(() => {
    if (!trip) {
      return;
    }
    detailsForm.setFieldsValue({ ...trip });
  }, [detailsForm, trip]);

  const visibleFiles = useMemo(
    () => files.filter((file) => !filesToDeletedIds.includes(file.id)),
    [files, filesToDeletedIds]
  );

  const handleOnResize = useCallback((width) => localSettings.setRecordPaneWidth(width), []);

  const handleHide = useCallback(() => {
    history.push("/");
    onHide();
  }, [history, onHide]);

  const dateField = tripFieldModel.getByName(TripFieldName.START_TIME)!;

  const handleFieldsChange = useDebouncedCallback(
    [
      () => {
        if (!trip) {
          return;
        }
        const updatedTripFields = detailsForm.getFieldsValue();
        if (trip.type !== updatedTripFields.type) {
          analytics.completed(["Change Classification"]);
        }
        if (!trip.note && updatedTripFields.note) {
          analytics.completed(["Add Notes"]);
        }
        if (!trip.tollFee && updatedTripFields.tollFee) {
          analytics.completed(["Add Tolls"]);
        }
        if (!trip.parkingFee && updatedTripFields.parkingFee) {
          analytics.completed(["Add Parking"]);
        }
        onUpdateTrip({ entity: { ...trip, ...updatedTripFields } });
      },
      1000,
    ],
    [onUpdateTrip]
  );

  const [isAddingFile, startAddingFileProcess, stopAddingFileProcess] = useBoolean(false, true);
  const [deleteConfirmationVisible, showDeleteConfirmation, hideDeleteConfirmation] = useBoolean(
    false,
    true
  );
  const handleShowDeleteConfirmation = useCallback(() => {
    showDeleteConfirmation();
    analytics.initiated(["Delete Trip"]);
  }, [showDeleteConfirmation, analytics]);
  const handleHideDeleteConfirmation = useCallback(() => {
    hideDeleteConfirmation();
    analytics.cancelled(["Delete Trip", "Cancel"]);
  }, [hideDeleteConfirmation, analytics]);
  const handleDeleteTrip = useCallback(
    (deleteReason: DeleteReason) => {
      analytics.completed(["Delete Trip", "Confirm"]);
      onDelete({ reason: getDeleteReasonDisplayName(intl, deleteReason) });
    },
    [onDelete, intl, analytics]
  );

  return (
    <ResizableDrawer
      className={cn(styles.container, className)}
      closable
      defaultWidth={defaultRecordPaneWidth}
      getContainer={zIndex ? undefined : getPageContentContainer()}
      mask={false}
      maxWidth={parseInt(consts.maxPaneWidth, 10)}
      onClose={handleHide}
      onResize={handleOnResize}
      open
      placement="right"
      title={trip ? dateField.getFormattedValueFor(trip) : ""}
      zIndex={zIndex}
    >
      {loading ? (
        <LoadingSpinner />
      ) : !trip ? (
        <NoAccess />
      ) : (
        <section className={styles.details}>
          <Subheader showAmounts={showAmounts} trip={trip} />
          <Map trip={trip} />
          <RouteDetails showAmounts={showAmounts} trip={trip} />
          <div className={styles.form} ref={formFieldsRef}>
            <Form<EditTripFormValues>
              colon={false}
              form={detailsForm}
              labelAlign="left"
              labelCol={{ span: 6 }}
              layout="horizontal"
              onFieldsChange={handleFieldsChange}
              onFinishFailed={handleFinishFailed}
            >
              <div className={styles.sectionName}>{intl.formatMessage(messages.tripDetails)}</div>
              <FormFields disabled={!trip?.accessStatus?.update} trip={trip} />
              <div className={cn(styles.sectionName, styles.filesSection)}>
                {intl.formatMessage(messages.files, { count: visibleFiles.length })}
                <AddOrCreateButton
                  disabled={!trip?.accessStatus?.update}
                  label={intl.formatMessage({
                    id: "recordPane.fileList.addButton",
                    defaultMessage: "Add",
                    description: "Title of a button to add a file to a record",
                  })}
                  onClick={startAddingFileProcess}
                />
              </div>
              <Files
                accessStatus={trip?.accessStatus}
                addingFile={isAddingFile}
                files={visibleFiles}
                onStopAddingFile={stopAddingFileProcess}
              />
              {trip?.accessStatus?.delete && (
                <Button
                  block
                  className={styles.deleteButton}
                  danger
                  disabled={deleting}
                  loading={deleting}
                  onClick={handleShowDeleteConfirmation}
                >
                  {intl.formatMessage(messages.delete, {
                    entityName: getEntityTypeDisplayName(intl, EntityType.TRIP, {
                      lowercase: false,
                    }),
                  })}
                </Button>
              )}
              {deleteConfirmationVisible && (
                <DeleteConfirmation
                  count={1}
                  onCancel={handleHideDeleteConfirmation}
                  onConfirm={handleDeleteTrip}
                />
              )}
            </Form>
          </div>
        </section>
      )}
    </ResizableDrawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  deleting: isDeleting(state),
  filesToDeletedIds: getFileIdsBeingDeleted(state),
  loading: isTripPreviewLoading(state),
  recordData: getTripPreviewRecordData(state),
  showAmounts: areMileageTrackingAmountsVisible(state),
});

const mapDispatchToProps = {
  onDelete: deleteTrip.request,
  onFetchPreviewData: fetchTripPreviewData.request,
  onUpdateTrip: updateTrip.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(TripRecordPane);
